import React, { useState } from 'react';
import { Button } from '../common/Button';
import toast from 'react-hot-toast';
import { submitSurveyResponse } from '../../services/feedbackService';
import { useAuth } from '../../context/AuthContext';

/**
 * Config-driven survey form.
 * Renders questions from a Firestore survey document.
 *
 * Supported question types:
 * - text: free text input
 * - currency: dollar amount input
 * - rating: 1-5 star rating
 * - multiple_choice: radio buttons from options array
 */
export default function SurveyForm({ survey, onSuccess }) {
  const { user, betaStatus } = useAuth();
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const updateAnswer = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const requiredQuestions = (survey.questions || []).filter((q) => q.required !== false);
  const allRequiredAnswered = requiredQuestions.every(
    (q) => answers[q.id] !== undefined && answers[q.id] !== ''
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allRequiredAnswered) {
      toast.error('Please answer all required questions');
      return;
    }

    setSubmitting(true);
    try {
      await submitSurveyResponse({
        surveyId: survey.id,
        answers,
        user,
        betaStatus,
      });
      toast.success('Survey submitted! Thank you for helping us improve.');
      onSuccess?.();
    } catch (error) {
      console.error('Failed to submit survey:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestion = (question) => {
    const isRequired = question.required !== false;

    switch (question.type) {
      case 'text':
        return (
          <textarea
            value={answers[question.id] || ''}
            onChange={(e) => updateAnswer(question.id, e.target.value)}
            placeholder="Your answer..."
            rows={3}
            className="w-full p-2 rounded-md bg-gray-700 border border-gray-600 text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
            disabled={submitting}
          />
        );

      case 'currency':
        return (
          <div className="flex items-center gap-2">
            <span className="text-gray-400 text-lg">$</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={answers[question.id] || ''}
              onChange={(e) => updateAnswer(question.id, e.target.value)}
              placeholder="0.00"
              className="w-32 p-2 rounded-md bg-gray-700 border border-gray-600 text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
              disabled={submitting}
            />
            <span className="text-gray-500 text-sm">per month</span>
          </div>
        );

      case 'rating':
        return (
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => updateAnswer(question.id, star)}
                className={`text-2xl transition-colors ${
                  star <= (answers[question.id] || 0)
                    ? 'text-yellow-400'
                    : 'text-gray-600 hover:text-gray-400'
                }`}
              >
                ★
              </button>
            ))}
          </div>
        );

      case 'multiple_choice':
        return (
          <div className="space-y-2">
            {(question.options || []).map((option) => (
              <label
                key={option}
                className={`flex items-center gap-2 p-2 rounded-md border cursor-pointer transition-colors ${
                  answers[question.id] === option
                    ? 'border-red-500 bg-red-900/20 text-gray-100'
                    : 'border-gray-600 bg-gray-700 text-gray-400 hover:border-gray-500'
                }`}
              >
                <input
                  type="radio"
                  name={question.id}
                  value={option}
                  checked={answers[question.id] === option}
                  onChange={() => updateAnswer(question.id, option)}
                  className="accent-red-500"
                  disabled={submitting}
                />
                <span className="text-sm">{option}</span>
              </label>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {survey.description && (
        <p className="text-sm text-gray-400">{survey.description}</p>
      )}

      {(survey.questions || []).map((question, index) => (
        <div key={question.id}>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {index + 1}. {question.text}
            {question.required !== false && <span className="text-red-400 ml-1">*</span>}
          </label>
          {renderQuestion(question)}
        </div>
      ))}

      <Button
        type="submit"
        variant="primary"
        disabled={submitting || !allRequiredAnswered}
        className="w-full"
      >
        {submitting ? 'Submitting...' : 'Submit Survey'}
      </Button>
    </form>
  );
}
